import { computed, inject, Injectable, Signal } from '@angular/core';
import { MapViewerService } from '@3ddv/ngx-dvm-internal';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { AppNodes } from '@models/enums/dvm/DvmNodes.enum';
import { VIEWER } from '@dvm.providers';
import { ViewService } from '@services/view.service';
import { UiService } from '@services/ui.service';
import { SeatmapDisplay } from '@models/types/components/SeatmapDisplay.type';
import { GeneralAdmission } from '@models/types/components/GeneralAdmission.type';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { Configuration } from '@models/interfaces/configuration/index.interface';
import { APP_CONFIG } from '@configuration/configuration';
import { VenueConfigurationsService } from './venue-configurations.service';


@Injectable({
  providedIn: 'root'
})
export class ViewDeterminationService {

  // CONFIGURATION
  private readonly config:                Signal<Configuration>            = inject(APP_CONFIG);
  private readonly dvmConfig:             Signal<Configuration['Dvm']>     = computed((): Configuration['Dvm'] => this.config().Dvm);
  private readonly uiconfig:              Signal<UiConfiguration>          = inject(UiService).uiConfig;


  // COMPUTED CONFIGURATION
  private readonly view3dDisplayEnabled:  Signal<boolean>                  = computed((): boolean => this.uiconfig().View3dDisplay.enabled);
  private readonly seatmapDisplay:        Signal<SeatmapDisplay>           = computed((): SeatmapDisplay => this.uiconfig().SeatmapDisplay);
  private readonly gaSections:            Signal<GeneralAdmission>         = computed((): GeneralAdmission => this.uiconfig().GeneralAdmission);
  private readonly isMultilevel:          Signal<boolean>                  = computed((): boolean => this.dvmConfig().viewer.isMultilevel ?? false);
  private readonly forceMainView:         Signal<boolean>                  = computed((): boolean => this.dvmConfig().viewer.forceMainView ?? false);


  // SERVICES
  private readonly viewer:                MapViewerService                 = inject(VIEWER);
  private readonly viewService:           ViewService                      = inject(ViewService);
  private readonly venueConfigs:          VenueConfigurationsService       = inject(VenueConfigurationsService);


  // PUBLIC METHODS

  /**
   * Establece la vista actual a partir del estado 3D, multilevel y tipos de nodo del mapa.
   *
   * @param is3d Indica si el pano 3D esta activo.
   */
  public setCurrentView(is3d: boolean): void {

    // Si es 3D, no se comprueban los nodos y se setea la vista a Pano.
    if(is3d) {
      return this.viewService.setView(AppViews.Pano);
    }

    if(this.isMultilevel()) {
      return this.viewService.setView(AppViews.Main);
    }

    // Solo afecta al primer mapa que se abre. Los demás se siguen calculando como siempre.
    // Mapa efectivo, no el principal: con otra venue configuration activa el mapa de entrada es el
    // de esa configuracion, y comparar contra el principal dejaria el flag sin efecto.
    if(this.forceMainView() && this.viewer.getMapId() === this.venueConfigs.viewerConfig().mapId) {
      return this.viewService.setView(AppViews.Main);
    }

    const nodeTypes:    string[]   = this.viewer.getTypesList().filter((type: string) => type !== AppNodes.Ga), // Conseguimos todos los nodos filtrando los GA.
          currentView:  AppViews   = this.determineView(nodeTypes);                                             // Determinamos la vista actual.

    this.viewService.setView(currentView);

  }


  /**
   * Determina si el mapa cargado debe tratarse como `Topview`, `Seatmap` o `Main`.
   *
   * @param nodes Tipos de nodo presentes en el mapa, sin General Admission.
   * @returns Vista DVM que corresponde a los nodos y layers actuales.
   */
  public determineView(nodes: string[]): AppViews {

    if (nodes.length === 1) {
      return nodes[0] === AppNodes.Section ? AppViews.Topview : AppViews.Seatmap;
    }

    if (nodes.some(node => /_nh/.test(node))) {
      return AppViews.Seatmap;
    }

    if(this.viewer.layers.getLayers().includes('mainlayer')) {
      return AppViews.Topview;
    }

    if(this.viewer.layers.getLayerLevels() === 2) {
      return AppViews.Main;
    }
    // Sin mainlayer y con un solo nivel, asumimos seatmap. Si algún mapa falla aquí, usa forceMainView.
    return AppViews.Seatmap;

  }


  //#region Seatmap + 3D conditions

  /**
   * Indica si un nodo debe abrir vista 3D segun configuracion y comportamiento GA.
   *
   * @param nodeId Identificador del nodo DVM.
   * @returns `true` si el nodo debe abrir pano 3D.
   */
  public apply3DView(nodeId: string): boolean {
    return this.view3dDisplayEnabled() && this.shouldSkipSeatmap(nodeId);
  }

  /**
   * Comprueba si una seccion esta configurada para comportarse como General Admission.
   *
   * @param nodeId Identificador del nodo DVM.
   * @returns `true` si la seccion debe abrir pano directamente como GA.
   */
  public isGAException(nodeId: string): boolean {
    const ga = this.gaSections();
    return ga.enabled && ga.data.includes(nodeId);
  }

  /**
   * Indica si un nodo debe seleccionarse en topview cuando no corresponde navegar a seatmap.
   *
   * @param nodeId Identificador del nodo DVM.
   * @returns `true` si la seleccion debe mantenerse en topview.
   */
  public selectNodeInTopview(nodeId: string): boolean {
    return !this.view3dDisplayEnabled() && this.shouldSkipSeatmap(nodeId);
  }

  /**
   * Indica si la navegacion debe omitir seatmap por configuracion o comportamiento GA.
   */
  private shouldSkipSeatmap(nodeId: string): boolean {
    return !this.seatmapDisplay().enabled || this.hasGeneralAdmissionBehavior(nodeId);
  }

  /**
   * Indica si el nodo DVM es GA nativo o una seccion configurada como GA.
   */
  private hasGeneralAdmissionBehavior(nodeId: string): boolean {
    return this.isGeneralAdmissionNode(nodeId) || this.isGAException(nodeId);
  }

  /**
   * Comprueba el tipo real del nodo cargado en DVM.
   */
  private isGeneralAdmissionNode(nodeId: string): boolean {
    return this.viewer.getNodeById(nodeId)?.type === AppNodes.Ga;
  }

  //#endregion

}
